// SPDX-License-Identifier: MIT
import * as z from "zod/mini";
import { font, rgba } from '../brand.ts';
import { inOutCubic, outCubic, outExpo } from '../core/ease.ts';
import { TAU, clamp, smoothstep } from '../core/math.ts';
import { DESIGN_H, DESIGN_W, defineScene } from '../scene.ts';
import * as kit from '../kit/index.ts';
import { drawGlyphs } from '../type.ts';
/**
 * Two-line headline: a staggered glyph reveal, then a bold accent line wiped in with a light bar.
 * Follows the ground (`kit.theme`); both ends of the accent gradient stay readable on it (≥ 3:1).
 */
export default defineScene({
    id: 'kinetic-headline',
    version: '0.2.0',
    aspects: ['16:9'],
    propsSchema: z.object({
        line1: z.string().check(z.maxLength(40)),
        line2: z.string().check(z.maxLength(32)),
        kicker: z.optional(z.string().check(z.maxLength(40))),
    }),
    glyphs: (p) => p.line1 + p.line2 + (p.kicker ?? ''),
    motion: (_p, env) => (env.localBeat < 2 ? 0.7 : 0.25),
    draw(ctx, props, env) {
        const th = kit.theme(env);
        const b = env.localBeat;
        const cx = DESIGN_W / 2;
        const cy = DESIGN_H / 2;
        ctx.fillStyle = th.bg;
        ctx.fillRect(0, 0, DESIGN_W, DESIGN_H);
        // Light rays rotating slowly behind the type (fainter on a light ground, where they are ink).
        const rayA = th.dark ? 1 : 0.45;
        ctx.save();
        ctx.globalCompositeOperation = th.light;
        ctx.translate(cx, cy + 40);
        const r = env.rng('rays');
        for (let i = 0; i < 14; i++) {
            const a0 = r() * TAU + env.local * (0.05 + r() * 0.08);
            const spread = 0.02 + r() * 0.05;
            ctx.fillStyle = rgba(i % 3 === 0 ? th.accent : th.primary, rayA * (0.05 + 0.05 * smoothstep(0, 1, b)));
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.arc(0, 0, 1500, a0, a0 + spread);
            ctx.closePath();
            ctx.fill();
        }
        ctx.restore();
        const push = 1 + 0.06 * inOutCubic(smoothstep(2.6, 4.2, b));
        ctx.save();
        ctx.translate(cx, cy);
        ctx.scale(push, push);
        ctx.translate(-cx, -cy);
        ctx.textBaseline = 'alphabetic';
        if (props.kicker) {
            ctx.font = font(env.brand, 500, 28, 'mono');
            ctx.letterSpacing = '8px';
            ctx.fillStyle = rgba(kit.readableTint(th.accent, th.bg, th.fg), smoothstep(0.1, 0.6, b));
            ctx.textAlign = 'center';
            ctx.fillText(props.kicker, cx, cy - 190);
            ctx.letterSpacing = '0px';
        }
        ctx.font = font(env.brand, 600, 92);
        ctx.fillStyle = th.fg;
        const w1 = drawGlyphs(ctx, props.line1, cx, cy - 40, 'center', (i, n) => {
            const k = outCubic(clamp((b - 0.15 - (i / Math.max(1, n)) * 0.6) / 0.45));
            return { dy: 46 * (1 - k), alpha: k };
        });
        env.report?.text('line1', [cx - w1 / 2, cy - 120, w1, 100], smoothstep(0.15, 1.2, b));
        ctx.font = font(env.brand, 800, 150);
        const w2 = ctx.measureText(props.line2).width;
        const wipe = outExpo(smoothstep(1.0, 2.0, b));
        if (wipe > 0) {
            const x0 = cx - w2 / 2;
            const grad = ctx.createLinearGradient(x0, 0, x0 + w2, 0);
            grad.addColorStop(0, kit.readableTint(th.accent, th.bg, th.fg));
            grad.addColorStop(1, th.accentText);
            ctx.save();
            ctx.beginPath();
            ctx.rect(x0 - 20, cy + 10, (w2 + 40) * wipe, 200);
            ctx.clip();
            ctx.fillStyle = grad;
            ctx.textAlign = 'center';
            ctx.fillText(props.line2, cx, cy + 150);
            ctx.restore();
            // The light bar riding the wipe edge.
            const edge = x0 - 20 + (w2 + 40) * wipe;
            const barA = 1 - smoothstep(1.7, 2.2, b);
            ctx.fillStyle = rgba(th.dark ? '#FFFFFF' : th.fg, barA);
            ctx.fillRect(edge - 3, cy + 20, 6, 160);
            ctx.fillStyle = rgba(th.accent, 0.9);
            ctx.fillRect(x0, cy + 180, w2 * wipe, 6);
            env.report?.text('line2', [x0, cy + 30, w2, 150], wipe);
        }
        ctx.restore();
    },
});
